import { ChatVisibility, EventType, type ScenarioStep, type SessionEvent } from "./fictiveTypes";
export { EventType, EventStatus, SenderRole } from "./fictiveTypes";
export type { SessionEvent, ScenarioStep, ScenarioFunction } from "./fictiveTypes";

export const APP_IDENTIFIER = "fictivereality-voice-chat";

export enum ChatState {
  Idle,
  Loading,
  Error, // render on top of chat so we can still interact with the history
  Connected,
  Offline, // we are offline, but can still chat
  Ended,
}

// Sync with StreamingMic.js
export enum MicState {
  INACTIVE = 0,
  LOADING = 1,
  ERROR = 2,
  MUTED = 3,
  NO_PERMISSION = 4,
  UNMUTED = 5,
}

export enum MicStateString {
  Inactive = "Inactive",
  Loading = "Loading",
  Error = "Error",
  NoPermission = "NoPermission",
  Muted = "Muted",
  Unmuted = "Unmuted",
}

export function micStateToString(state: MicState): MicStateString {
  switch (state) {
    case MicState.INACTIVE:
      return MicStateString.Inactive;
    case MicState.LOADING:
      return MicStateString.Loading;
    case MicState.ERROR:
      return MicStateString.Error;
    case MicState.MUTED:
      return MicStateString.Muted;
    case MicState.UNMUTED:
      return MicStateString.Unmuted;
    case MicState.NO_PERMISSION:
      return MicStateString.NoPermission;
    default:
      return MicStateString.Inactive;
  }
}

export enum DisplayMode {
  hidden = "hidden",
  widget = "widget",
  floating = "floating",
  mounted = "mounted",
  iframe = "iframe",
}

export interface SessionData {
  /** A valid API Key from the provided organization. */
  apiKey?: string;
  /** Whether to ask the user for clear consent before starting chats. */
  askConsent?: boolean;
  /** Initial bot-audio preference for this mount. Runtime changes are owned by the Voice Chat menu. */
  botAudioEnabled?: boolean;
  /** Whether to automatically start the scenario when configured */
  autostart?: boolean;
  /** Whether to show debug information */
  debug?: boolean;
  /** Whether bot transcript text should be revealed word-by-word as it is spoken. */
  showWordsAsTheyAreSpoken?: boolean;
  /** Whether to hide the text input field */
  disableTextInput?: boolean;
  /** Whether to completely disable the chat log (not accessible) */
  disableChatLog?: boolean;
  /** Whether to fetch the scenario data from REST endpoint */
  fetchScenario?: boolean;
  /** Returns a current ID token identifying a user of Fictive Reality */
  getIdToken?: () => Promise<string | undefined>;
  /** Whether the microphone should start unmuted e.g. recording */
  microphoneStartUnmuted?: boolean;
  /** Unique identifier for the organization to bill */
  organizationId?: string;
  /** Unique identifier for the player */
  playerId?: string;
  /** Unique identifier for the scenario */
  scenarioId?: string;
  /** Language locale code for the UI, will follow the scenario */
  uiLocale?: string;
  /** Text input visibility */
  chatInputVisibility?: ChatVisibility;
  /** Chat log visibility */
  chatLogVisibility?: ChatVisibility;
}

// TODO this type might merge completely with the Scenario object from Fictive schema
export interface ScenarioData {
  /** URL to the 3D avatar model, as a GLB or GLTF file */
  avatarUrl?: string;
  /** URL to the background image as 360 .EXR, .HDR or 2D regular image file */
  backgroundUrl?: string;
  /** Name of the bot displayed in the UI */
  botName?: string;
  /** URL to the bot's avatar image, as a 2D image file */
  botImageUrl?: string;
  /** Camera position string relative to avatar (format: "x,y,z,fov") where x,y,z are in meters and fov is in degrees */
  cameraPosition?: string;
  /** Extra text to add to the consent message, if shown. */
  consentMessage?: string;
  /** Custom data to pass to the scenario */
  customScenarioData?: Record<string, unknown>;
  /** Description text shown before chat starts */
  description?: string;
  /** The ID of the initial chat script to start, if not the first */
  initialStep?: string;
  /** Language locale code (e.g. 'en-US') */
  locale?: string;
  /** If this scenario steps should be evaluated offline */
  offline?: boolean;
  /** Steps (state machine) that this scenario uses */
  steps?: ScenarioSteps;
  /** Title displayed in the chat header */
  title?: string;
  /** A message to show above the widget icon to catch attention */
  widgetHint?: string;
}

export interface VoiceChatProps {
  /** Session specific configuration */
  session?: SessionData;
  /** Scenario specific configuration */
  scenario?: ScenarioData;
  /** Whether the user's camera feed should be captured and rendered locally */
  enableUserCamera?: boolean;

  // Component specific options, e.g. how it integrates and renders
  /** How the chat should be displayed */
  displayMode?: DisplayMode;
  /** Callback to call before navigating to a link presented in the chat. */
  navigateCallback?: (url: string) => boolean;
  /**
   * Handlers for client tools this application runs itself, by tool name. A handler replaces
   * VoiceChat's own handler for that name. Read once when VoiceChat mounts; later changes are ignored.
   */
  clientToolHandlers?: ClientToolHandlers;
  /** Called when a client tool call arrives, whichever handler answers it. It cannot answer the call. */
  onClientTool?: (tool: { name?: string; arguments?: Record<string, unknown> }) => void;
  /** Called once when a completed session ends. */
  onSessionEnded?: (sessionId: string) => void;
  /** The server URL that will be used to build REST and Websocket endpoint URLs */
  serverUrl?: string;
  /** URL to a help page, if not given no help menu item will be shown */
  helpUrl?: string;
  /** URL or generator function for creating a session URL */
  sessionReportUrl?: string | ((sessionId: string) => string);
  /** The display mode the widget opens into when clicked, which can be minimized back to the widget. */
  openDisplayMode?: DisplayMode;
  /**
   * Share (0-1) of the height that the avatar gets, the chat log gets the rest. Equal if not given.
   * Ignored in fullscreen. For hosts where the chat log matters more, e.g. a tall sidebar.
   */
  avatarShare?: number;
  /** Function to update the display mode */
  setDisplayMode?: (mode: DisplayMode) => void;
  /** Target DOM element for the 'mounted' display mode */
  mountInElement?: HTMLElement;
}

export type ScenarioSteps = {
  [id: string]: ScenarioStep;
};

export interface LipsyncEvent {
  audio_offset: number;
  viseme_id: string;
}

// Sync this with Convai server, until we have it in common schema
export interface WebsocketMessage {
  type: string;
  text?: string;
  id?: string; // only used for PartialTranscript at the moment
  sender: "human" | "bot";
  data?: string; // Used for audio data only
  lipsync_events?: LipsyncEvent[];
  timestamp?: number;
  metadata: SessionEvent;
}

export interface ChoiceOption {
  id: string;
  title: string;
}

/** A client tool's answer. An object is sent to the AI actor; `undefined` sends nothing. */
export type ClientToolResult = Record<string, unknown> | undefined;

/**
 * Runs one client tool call and resolves with its answer.
 *
 * Resolve with `undefined` only to leave the call unanswered: convai then answers the AI actor itself
 * when its timeout runs out. If the handler throws, the AI actor gets an error instead.
 */
export type ClientToolHandler = (args: Record<string, unknown>) => Promise<ClientToolResult>;

/** Client tool handlers by tool name. */
export type ClientToolHandlers = Record<string, ClientToolHandler>;

/** A choice the AI actor is waiting on, put to the player by a client-run tool. */
export interface ChoiceRequest {
  message: string;
  options: ChoiceOption[];
}

export function isInputChatMessage(msg?: SessionEvent): boolean {
  return (
    msg?.eventType === EventType.ChoiceInput ||
    msg?.eventType === EventType.NumberInput ||
    msg?.eventType === EventType.TextInput ||
    msg?.eventType === EventType.ImageInput
  );
}

export type VoiceChatStringConfigType = "string" | "boolean" | "url" | "select";

export interface VoiceChatStringConfigField {
  key: string;
  type: VoiceChatStringConfigType;
  label: string;
  description?: string;
  placeholder?: string;
  default?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
}

// Needs to be kept in sync with VoiceChatProps and helpers.ts/parseStringProps and ensureVoiceChatDefaults
// NOTE the order is the order the options will be displayed in the configurator
export const VOICE_CHAT_STRING_CONFIG_SCHEMA: VoiceChatStringConfigField[] = [
  {
    key: "organizationId",
    type: "string",
    label: "Organization ID",
    required: true,
    description: "Unique identifier for the organization to bill, copy from Organization Settings",
  },
  {
    key: "scenarioId",
    type: "string",
    label: "Scenario ID",
    required: true,
    description:
      "Unique identifier for the scenario. Controls which AI actor and how it behaves. Copy from Scenario Editor or Play URL",
  },
  {
    key: "apiKey",
    type: "string",
    label: "API Key",
    required: true,
    description:
      "A valid API Key from the selected organization. Can be tracked and deleted to stop unauthorized usage. Copy from below 'Shareable signup links' in Admin Dashboard",
  },
  {
    key: "displayMode",
    type: "select",
    label: "Display Mode",
    description:
      "How the chat should be displayed from start. Defaults to Widget. Mounted and IFRAME depends on how VoiceChat is integrated.",
    options: [
      { value: "hidden", label: "Hidden" },
      { value: "widget", label: "Widget (small button)" },
      { value: "floating", label: "Floating" },
      { value: "mounted", label: "Mounted" },
      { value: "iframe", label: "IFRAME" },
    ],
    default: "widget",
  },
  {
    key: "serverUrl",
    type: "url",
    label: "Server URL",
    description: "The URL of the server",
    placeholder: "(automatically set)",
  },
  {
    key: "locale",
    type: "select",
    label: "Locale",
    description: "Initial language for user interface. Can always be changed by user from settings.",
    options: [
      { value: "en-US", label: "English (US)" },
      { value: "sv-SE", label: "Swedish (SE)" },
    ],
    default: "en-US",
  },
  {
    key: "fetchScenario",
    type: "boolean",
    label: "Fetch Scenario",
    description: "Whether to fetch the scenario data from server instead of configuring manually.",
    default: "true",
  },
  {
    key: "botAudioEnabled",
    type: "boolean",
    label: "Bot audio",
    description: "Whether bot audio should playback automatically",
    default: "true",
  },
  {
    key: "askConsent",
    type: "boolean",
    label: "Ask Consent",
    description: "Whether to ask the user for clear consent before starting chats.",
    default: "false",
  },
  {
    key: "autostart",
    type: "boolean",
    label: "Autostart",
    description: "Whether to automatically start the scenario when configured. Turn off to let users click when ready.",
    default: "false",
  },
  {
    key: "debug",
    type: "boolean",
    label: "Debug",
    description: "Whether to show extra debug information and allow camera controls.",
    default: "false",
  },
  {
    key: "disableTextInput",
    type: "boolean",
    label: "Disable Text Input",
    description: "Whether to hide the text input field",
    default: "false",
  },
  {
    key: "disableChatLog",
    type: "boolean",
    label: "Disable Chat Log",
    description: "Whether to hide the chat log",
    default: "false",
  },
  {
    key: "microphoneStartUnmuted",
    type: "boolean",
    label: "Microphone Start Unmuted",
    description: "Whether the microphone should start unmuted e.g. recording",
    default: "false",
  },
  {
    key: "enableUserCamera",
    type: "boolean",
    label: "Enable User Camera",
    description: "Whether to capture and show the user's camera feed locally",
    default: "false",
  },
  {
    key: "offline",
    type: "boolean",
    label: "Offline",
    description: "If this scenario should be evaluated offline. Will deactivate mic. Only for scenarios with steps!",
    default: "false",
  },
  {
    key: "widgetHint",
    type: "string",
    label: "Widget Hint",
    description: "A message to show above the widget icon to catch attention. Only relevant if shown as widget.",
  },
  {
    key: "helpUrl",
    type: "url",
    label: "Help URL",
    description: "URL to a help page, if not given no help menu item will be shown",
  },
  {
    key: "playerId",
    type: "string",
    label: "Player ID",
    description: "Unique identifier for the player. NOT SUPPORTED",
  },
  {
    key: "avatarUrl",
    type: "url",
    label: "Avatar URL",
    placeholder: "(read from scenario when fetched)",
    description: "URL to the 3D avatar model, as a GLB or GLTF file",
  },
  {
    key: "backgroundUrl",
    type: "url",
    label: "Background URL",
    placeholder: "(read from scenario when fetched)",
    description: "URL to the background image as 360 .EXR, .HDR or 2D regular image file",
  },
  {
    key: "botName",
    type: "string",
    label: "Bot Name",
    placeholder: "(read from scenario when fetched)",
    description: "Name of the bot displayed in the UI",
  },
  {
    key: "botImageUrl",
    type: "url",
    label: "Bot Image URL",
    placeholder: "(read from scenario when fetched)",
    description: "URL to the bot's avatar image, as a 2D image file",
  },
  {
    key: "consentMessage",
    type: "string",
    label: "Consent Message",
    placeholder: "(read from scenario when fetched)",
    description: "Extra message to add to the consent text, if shown.",
  },
  {
    key: "description",
    type: "string",
    label: "Description",
    placeholder: "(read from scenario when fetched)",
    description: "Description text shown before chat starts",
  },
  {
    key: "initialStep",
    type: "string",
    label: "Initial Step",
    placeholder: "(read from scenario when fetched)",
    description:
      "The ID of the initial step of a scenario to start with, if not the first. Only for scenarios with steps.",
  },
  {
    key: "title",
    type: "string",
    label: "Title",
    placeholder: "(read from scenario when fetched)",
    description: "Title displayed in the chat header",
  },
];

export interface IVoiceChatAdapter {
  dispose(): Promise<void>;
  sendMessage(message: SessionEvent): Promise<void>;
  toggleRecording(): void;
  setMicrophoneDevice(deviceId: string): void;
  setBotAudioEnabled?(enabled: boolean): void;
  getMicVu(): number;
  getSpeakerVu(): number;
  getMicState(): MicState;
  connect(connectInfo?: ConnectInfo): Promise<void>;
  refreshMicDevices(): Promise<void>;
}

export interface MicrophoneDevice {
  deviceId: string;
  label: string;
}

export interface MicrophoneDevicesEvent {
  devices: MicrophoneDevice[];
  selected: string;
}

export interface VoiceChatRuntime {
  remoteAudioElement?: HTMLAudioElement | null;
}

export interface ConnectInfo {
  url: string;
  token?: string;
  transport?: string;
  iceServers: RTCIceServer[];
}

export interface ServerStartResponse {
  sessionId: string;
  message?: string;
  connectInfo: ConnectInfo;
  /** Present on error responses (e.g. 503 capacity_exhausted) */
  error?: string;
  /** Suggested retry delay in seconds (present on 503 capacity_exhausted) */
  retryAfterSeconds?: number;
}
